import { Component, OnInit, ViewChild, HostListener, OnDestroy, ChangeDetectorRef, ElementRef } from '@angular/core';
import { ABCisApi } from 'src/app/handlers/apiHandler.service';
import { ApiService } from 'src/app/handlers/apiHandler.service';
import { JobConfiguration } from 'src/app/models/job';
import { HttpStatus, OcrJob, OcrChannel } from 'src/app/constants/constants';
import { ToastrService } from 'ngx-toastr';
import { LoggerService } from 'src/app/handlers/logger.service';
import { SharedService } from 'src/app/shared/shared.service';
import { HttpErrorResponse } from '@angular/common/http';
import { PdfjsViewerComponent, PdfJsViewerConfig, ExtractedText } from 'src/app/components/pdfjs-viewer/pdfjs-viewer.component';
import { environment } from 'src/environments/environment.dev';
import { MatDialog } from '@angular/material/dialog';
import { SplitterResizeEndEvent } from 'primeng/splitter';
import {GridData, GridCell, GridExtractionService, GridHighlightLocation} from 'src/app/services/grid-extraction.service';
import { Subscription } from 'rxjs';
import { StatementGridExtractionService } from 'src/app/services/statement-grid-extraction.service';

interface DocumentHeader {
  documentId: number;
  documentTotal: number | null;
}

interface StatementLineItem {
  statementLineItemId: number;
  documentId: number;
  documentDate: string;
  documentType: string;
  documentNumber: string;
  amount: number;
}

interface DocumentDetailsResponse {
  supplier: SupplierInfo;
  channelData: DocumentHeaderLineItem[];
}

interface SupplierInfo {
  globalSupplierId: number;
  supplierName: string;
  skipLineItems: boolean;
}

interface ChannelOption {
  id: string;
  name: string;
  description: string;
}

interface DocumentHeaderLineItem {
  channelId: number;
  channelName: string;
  headerData: DocumentHeader;
  lineItemData: StatementLineItem[];
}

interface LineItem {
  [key: string]: any;
  id: number;
  statementLineItemId?: number;
  documentDate: string;
  documentType: string;
  documentNumber: string;
  amount: number | null;
  gridCellLocations?: Record<string, GridHighlightLocation | undefined>;
}

interface TesseractWord {
  text: string;
  x0: number;
  x1: number;
  top: number;
  bottom: number;
  height: number;
  width: number;
  direction: string;
  doctop: number;
  upright: boolean;
}

interface PytesseractWord {
  char: string;
  x0: number;
  x1: number;
  top: number;
  bottom: number;
}

interface TesseractPage {
  pageNumber: number;
  width: number;
  height: number;
  text?: string;
  words?: TesseractWord[];
  characters?: PytesseractWord[];
}

export interface TesseractOutput {
  pageCount: number;
  pages: TesseractPage[];
}

interface TableColumn {
  field: string;
  header: string;
  style: {};
  type: 'string' | 'integer' | 'decimal' | 'boolean';
  class?: string;
}

interface ColumnPreset {
  name: string;
  columnFields: string[];
}


@Component({
  selector: 'app-statement-line-item-job',
  standalone: false,
  templateUrl: './statement-line-item-job.component.html',
  styleUrls: ['./statement-line-item-job.component.scss'],
  providers: [
    StatementGridExtractionService,
    { provide: GridExtractionService, useExisting: StatementGridExtractionService }
  ]
})
export class StatementLineItemJobComponent implements OnInit, OnDestroy {

  @ViewChild('pdfViewer', {static: false}) pdfViewer!: PdfjsViewerComponent;
  @ViewChild('pdfPanelContainer', { static: false }) pdfPanelContainer?: ElementRef<HTMLDivElement>;

  webUiChannelId = 5;
  documentId: number | null = null;

  // Job properties
  currentInputDocument: JobConfiguration;
  isloading = false;
  uuid: string;
  selectedChannelId: number;
  isJobControlsHidden = false;
  supplierSkipLineItems = false;

  // Form fields
  // TODO - Kasun - should the default values be null, instead of '' or 0
  supplierInfo: { value: number; label: string } | null = null;
  supplierName = '';
  documentTotal: number | null = null;

  // Table data
  lineItems: LineItem[] = [];

  // Total validation
  statementAmountTotal = 0;
  statementAmountTotalValid = false;
  statementAmountVariance = 0;
  validationThreshold = 0.02;

  // OCR functionality
  currentCellEdit: { rowData: any; field: string; index: number; } | null = null;

  // column selection
  displayedColCount = 1;
  displayedColumns: TableColumn[] = [];
  selectedPreset = {};

  // Keyboard navigation state
  private isHelpVisible = false;
  private helpToastRef: any = null;

  // page layout controls - panel sizes
  equalSized = [50,50];
  leftCollapsed = [0.1,99.9];
  rightCollapsed = [99.9,0.1];
  currentPanelSizes = [...this.equalSized];
  private preferredPanelSizes = [...this.equalSized];
  private readonly panelSizeStorageKey = 'line-item-job-panel-sizes';

  // PDF properties
  pdfUrl = '';
  pdfViewerConfig: PdfJsViewerConfig = {
    showControls: true,
    showNavigation: true,
    showZoomControls: true,
    showActionControls: true,
    showDownloadPdf: true,
    showOpenInNewWindow: true,
    showMinimizePdf: true,
    initialScale: 1.0,
    maxScale: 5.0,  // Increased to allow 500% zoom
    minScale: 0.3,
    zoomIncrement: 0.25,
    downloadFileName: 'invoice-document.pdf',
    continuousView: false
  };
  isPdfInPopoutWindow = false;
  pdfPanelPlaceholderHeight = 0;
  private popoutWindow: Window | null = null;
  private popoutWindowKeydownListener?: (event: KeyboardEvent) => void;
  private popoutWindowUnloadListener: (() => void) | null = null;
  private pdfPanelHostParent: HTMLElement | null = null;
  private pdfPanelHostNextSibling: ChildNode | null = null;
  private readonly tooltipSuppressionClass = 'pdf-popout-tooltips-disabled';

  showColumnSwapDialog = false;
  swapColumn1: string = '';
  swapColumn2: string = '';
  swapColumnOptions: { label: string; value: string }[] = [];
  showClearColumnDialog = false;
  clearColumnOptions: { label: string; value: string }[] = [];
  selectedClearColumn: string = '';

  // Grid extraction panel
  showGridExtractionPanel = false;
  gridExtractionPanelData: GridData | null = null;
  gridExtractionPanelPageKey: string | null = null;
  gridImportValidationMessage = '';

  get lineItemColumns(): TableColumn[] {
    return this.allColumns;
  }

  // Table columns definition
  allColumns: TableColumn[] = [
    {field: 'documentDate', header: 'Document Date', style: {width: '120px'}, type: 'string'},
    {field: 'documentType', header: 'Document Type', style: {width: '120px'}, type: 'string'},
    {field: 'documentNumber', header: 'Document Number', style: {width: '160px'}, type: 'string'},
    {field: 'amount', header: 'Amount', style: {width: '120px'}, class: 'td-right', type: 'decimal'}
  ];

  // Initial selected columns & ensure column order is retained
  columnPresets: ColumnPreset[] = [
    { name: 'Statement Fields', columnFields: ['documentDate', 'documentType', 'documentNumber', 'amount'] },
    { name: 'Document + Amount', columnFields: ['documentNumber', 'amount'] },
    { name: 'All Columns', columnFields: this.allColumns.map(col => col.field) }
  ];

  // Channel
  private allChannelData: DocumentHeaderLineItem[] = [];
  availableChannels: ChannelOption[] = []; // Will be populated from backend response

  selectedChannel = '';
  isChannelLoading = false;

  // Unsave tracking
  private hasUnsavedTableChanges = false;

  private originalDataSnapshot: Map<string, any> = new Map();
  private changedCells: Set<string> = new Set(); // Track which cells are different

  // For confirmation resolve
  private currentConfirmationResolve: ((value: boolean) => void) | null = null;

  tesseractData: TesseractOutput | null = null;

  // Replace showStopConfirmation with generic confirmation
  showSubmitConfirmation = true; // Set to true to always show confirmation

  showConfirmation = false;
  confirmationConfig = {
    title: '',
    message: '',
    confirmText: '',
    confirmColor: 'primary',
    showCancel: true,
    action: null as (() => void) | null
  };
  private sharedChannelSubscription?: Subscription;
  private readonly windowMessageHandler = (event: MessageEvent) => this.handleWindowMessage(event);

  constructor(
    private apiHandler: ApiService,
    private gridExtractionService: GridExtractionService,
    private toastr: ToastrService,
    private logger: LoggerService,
    private sharedService: SharedService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog
  ) {
  }

  async ngOnInit() {
    this.sharedChannelSubscription = this.sharedService.sharedChannel.subscribe(channel => this.selectedChannelId = channel);
    // Add window message listener
    window.addEventListener('message', this.windowMessageHandler);
    this.restorePanelSizesFromStorage();
  }

  ngOnDestroy(): void {
    if (this.helpToastRef) {
      this.toastr.clear(this.helpToastRef.toastId);
      this.helpToastRef = null;
    }
    this.isHelpVisible = false;
    window.removeEventListener('message', this.windowMessageHandler);
    this.sharedChannelSubscription?.unsubscribe();
    this.sharedChannelSubscription = undefined;
    this.cleanupSessionStorage();
    this.closePdfPopoutWindow(true);
    this.setTooltipSuppression(false);
  }

  private handleWindowMessage(event: MessageEvent) {
    if (event.origin !== window.location.origin) {
      return;
    }
    if (!event.data || typeof event.data !== 'object') {
      return;
    }
    if (event.data.type === 'textExtracted') {
      this.onTextExtracted(event.data.data);
    }
  }

  private applyPopoutState(isActive: boolean): void {
    this.isPdfInPopoutWindow = isActive;
    this.pdfViewerConfig = {
      ...this.pdfViewerConfig,
      showOpenInNewWindow: !isActive,
      showMinimizePdf: !isActive
    };
    this.setTooltipSuppression(isActive);
    this.cdr.detectChanges();
  }

  private setTooltipSuppression(enabled: boolean): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.body.classList.toggle(this.tooltipSuppressionClass, enabled);
  }

  private updateGridModeAvailability(): void {
    this.pdfViewerConfig = {
      ...this.pdfViewerConfig,
      disableGridMode: this.supplierSkipLineItems
    };
  }

  private openPdfInPopoutWindow(): void {
    if (this.isPdfInPopoutWindow) {
      return;
    }

    if (!this.pdfUrl) {
      this.toastr.info('Load a PDF before opening it in a new window.');
      return;
    }

    const pdfElement = this.pdfPanelContainer?.nativeElement;
    if (!pdfElement || !pdfElement.parentElement) {
      return;
    }

    this.pdfPanelHostParent = pdfElement.parentElement;
    this.pdfPanelHostNextSibling = pdfElement.nextSibling;
    this.pdfPanelPlaceholderHeight = pdfElement.getBoundingClientRect().height || 320;

    const windowWidth = Math.max(pdfElement.clientWidth, 900);
    const windowHeight = Math.max(pdfElement.clientHeight, 700);

    const features = `width=${windowWidth},height=${windowHeight},resizable=yes,scrollbars=yes`;
    const newWindow = window.open('', 'invoicePdfViewerPopout', features);

    if (!newWindow) {
      this.toastr.error('Unable to open a new window for the PDF viewer. Please allow pop-ups.');
      this.pdfPanelHostParent = null;
      this.pdfPanelHostNextSibling = null;
      this.pdfPanelPlaceholderHeight = 0;
      this.expandPdfPanel();
      return;
    }

    this.setupPopoutWindowDocument(newWindow);
    newWindow.document.body.appendChild(pdfElement);
    pdfElement.classList.add('pdf-popout-active');

    this.popoutWindow = newWindow;
    this.applyPopoutState(true);

    this.popoutWindowKeydownListener = (event: KeyboardEvent) => {
      this.pdfViewer?.handlePdfHotkeys(event);
    };
    newWindow.addEventListener('keydown', this.popoutWindowKeydownListener, true);

    this.popoutWindowUnloadListener = () => this.closePdfPopoutWindow(false);
    newWindow.addEventListener('beforeunload', this.popoutWindowUnloadListener);

    this.schedulePdfZoomToFit();
  }

  private setupPopoutWindowDocument(targetWindow: Window): void {
    const doc = targetWindow.document;
    doc.open();
    doc.write('<!DOCTYPE html><html><head><title>Invoice PDF Viewer</title></head><body></body></html>');
    doc.close();
    doc.title = 'Invoice PDF Viewer';
    doc.head.innerHTML = '';
    const styleNodes = document.head.querySelectorAll('style, link[rel="stylesheet"]');
    styleNodes.forEach(node => {
      const clone = node.cloneNode(true) as HTMLElement;
      doc.head.appendChild(clone);
    });

    doc.body.classList.add('pdf-popout-body');
    doc.body.style.margin = '0';
    doc.body.style.background = '#0b1121';
    doc.body.style.display = 'flex';
    doc.body.style.alignItems = 'stretch';
    doc.body.style.justifyContent = 'stretch';
    doc.body.style.minHeight = '100vh';
  }

  private closePdfPopoutWindow(shouldCloseWindow: boolean): void {
    if (!this.isPdfInPopoutWindow && !this.popoutWindow) {
      return;
    }

    const pdfElement = this.pdfPanelContainer?.nativeElement;
    if (pdfElement && this.pdfPanelHostParent) {
      if (this.pdfPanelHostNextSibling && this.pdfPanelHostParent.contains(this.pdfPanelHostNextSibling)) {
        this.pdfPanelHostParent.insertBefore(pdfElement, this.pdfPanelHostNextSibling);
      } else {
        this.pdfPanelHostParent.appendChild(pdfElement);
      }
      pdfElement.classList.remove('pdf-popout-active');
    }

    if (this.popoutWindow && this.popoutWindowKeydownListener) {
      this.popoutWindow.removeEventListener('keydown', this.popoutWindowKeydownListener, true);
      this.popoutWindowKeydownListener = undefined;
    }

    if (this.popoutWindow && this.popoutWindowUnloadListener) {
      this.popoutWindow.removeEventListener('beforeunload', this.popoutWindowUnloadListener);
      this.popoutWindowUnloadListener = null;
    }

    if (this.popoutWindow && shouldCloseWindow) {
      try {
        this.popoutWindow.close();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : `${error}`;
        this.logger.warn(`Unable to close PDF pop-out window automatically. ${errorMessage}`);
      }
    }

    this.popoutWindow = null;
    this.pdfPanelHostParent = null;
    this.pdfPanelHostNextSibling = null;
    this.pdfPanelPlaceholderHeight = 0;
    this.applyPopoutState(false);
  }

  // ============================== Data/File loading/submission handlers -
  async getNextFile(): Promise<void> {
    this.isloading = true;
    // clear tesseract cache
    this.tesseractData = null;
    this.gridExtractionService.cacheTesseractData(this.tesseractData);
    try {
      const httpResponse = await this.apiHandler.getABCisRequest<JobConfiguration>(
        ABCisApi.Ocr,
        `job/${OcrJob.EXTRACT_STATEMENT_HEADER_AND_LINE_ITEM_DATA}/next-document?channelId=${this.selectedChannelId || OcrChannel.WEB_UI}`
      );

      if (httpResponse.status != HttpStatus.NO_CONTENT) {
        this.currentInputDocument = httpResponse.body;
        this.configureInputDocument();
        // Trigger GCS call for the tesseract output
        await this.fetchTesseractOutput(this.currentInputDocument.uuid);
      } else {
        this.currentInputDocument = undefined;
        this.toastr.info('No more files available for Line Item job');
      }
    } catch (error) {
      this.logger.error(`Failed to get next file: ${error.message || error}`);
      const httpError = error as HttpErrorResponse;
      this.toastr.error(httpError.error?.message || 'Failed to fetch next file');
    } finally {
      this.isloading = false;
    }
  }

  private async fetchTesseractOutput(uuid: string): Promise<void> {
    try {
      const httpResponse = await this.apiHandler.getABCisRequest<TesseractOutput>(
        ABCisApi.Ocr,
        `document/${uuid}/tesseract-ocr-output`
      );
      if (httpResponse.status === HttpStatus.OK && httpResponse.body) {
        this.tesseractData = httpResponse.body;
        this.gridExtractionService.cacheTesseractData(this.tesseractData);
        this.logger.debug(`Loaded tesseract OCR data for document: ${uuid}`);
      } else {
        this.tesseractData = null;
        this.gridExtractionService.cacheTesseractData(this.tesseractData);
        this.logger.warn('Tesseract OCR data unavailable.');
      }
    } catch (err) {
      this.logger.error(`Failed to fetch tesseract OCR data: ${err.message || err}`);
      this.tesseractData = null;
      this.gridExtractionService.cacheTesseractData(this.tesseractData);
    }
  }

  async configureInputDocument(): Promise<void> {
    // Hide job control on load
    if (!this.isJobControlsHidden) {
      this.toggleJobControls();
    }

    this.uuid = this.currentInputDocument.uuid;

    // Reset form data
    this.resetFormData();
    this.gridExtractionService.clearColumnTemplateSnapshot();

    this.pdfViewerConfig = {
      ...this.pdfViewerConfig,
      downloadFileName: `${this.currentInputDocument.uuid}.pdf`,
      initialScale: 1.0,
    };

    try {

      const [pdfUrl, , ] = await Promise.all([
        this.getPdfUrl(this.currentInputDocument.uuid),
        this.loadDocumentHeaderAndLineItems(this.currentInputDocument.uuid)
      ]);

      this.pdfUrl = pdfUrl;

      setTimeout(() => {
        if (this.pdfViewer) {
          this.pdfViewer.loadPdf(this.pdfUrl, this.pdfViewerConfig);
        }
      }, 200);


    } catch (error) {
      this.logger.error(`Failed to get PDF URL: ${error.message || error}`);
    }

    setTimeout(() => {
      const input = document.getElementById('documentTotal') as HTMLInputElement;
      input?.focus();
      input?.select();
    }, 50);
  }

  private async loadDocumentHeaderAndLineItems(uuid: string): Promise<void> {
    try {
      this.isloading = true;

      const response = await this.apiHandler.getABCisRequest<DocumentDetailsResponse>(
        ABCisApi.Support,
        `document/statement-header-line-items/${uuid}`
      );

      if (response.status === 200 && response.body) {
        const documentDetails = response.body;

        // Cache all channel data
        this.allChannelData = documentDetails.channelData || [];

        // Populate available channels dropdown
        this.availableChannels = this.allChannelData.map(channel => ({
          id: channel.channelId.toString(),
          name: channel.channelName,
          description: `${channel.channelName} (${channel.lineItemData?.length || 0} items)`
        }));

        // Use first available channel as default
        if (this.allChannelData.length > 0) {
          const lastChannel = this.allChannelData[this.allChannelData.length - 1];

          // Populate header data
          if (lastChannel.headerData) {
            this.populateHeaderData(lastChannel.headerData);
          }

          // Populate line items
          if (lastChannel.lineItemData && lastChannel.lineItemData.length > 0) {
            this.populateLineItems(lastChannel.lineItemData);
          }

          // Set selected channel
          this.selectedChannel = lastChannel.channelId.toString();
        }

        // Update supplier info
        if (documentDetails.supplier) {
          this.supplierInfo = {
            value: documentDetails.supplier.globalSupplierId,
            label: documentDetails.supplier.supplierName
          };
          this.supplierName = documentDetails.supplier.supplierName;
          this.supplierSkipLineItems = documentDetails.supplier.skipLineItems;
          this.updateGridModeAvailability();
        }

      } else {
        this.toastr.info('No existing data found, starting with blank form');
      }

    } catch (error) {
      this.logger.error(`Failed to load document data: ${error.message || error}`);

      if (error.status !== 404) {
        this.toastr.warning('Could not load existing document data, starting with blank form');
      }

      this.resetFormData();
    } finally {
      this.isloading = false;
    }
  }

  private populateHeaderData(headerData: DocumentHeader): void {
    this.documentId = headerData.documentId;
    this.documentTotal = headerData.documentTotal ?? null;
  }

  private populateLineItems(lineItemData: StatementLineItem[]): void {
    this.lineItems = lineItemData.map((item, index) => ({
      ...item,
      id: item.statementLineItemId || Date.now() + index,
      gridCellLocations: undefined
    }));

    this.captureOriginalSnapshot();
    this.validateStatementAmountTotal();

    // Display the columns with at least one non-empty/not-null data point
    this.displayedColumns = this.allColumns.filter(col => {
      return this.lineItems.some(item => {
        const value = item[col.field];
        return value !== null && value !== undefined && value !== '';
      });
    });
    this.onSelectedColumnsChange();

    // Reset flag when loading new data
    this.hasUnsavedTableChanges = false;
  }

  // ============================== Confirmation dialog handlers
  showConfirmationDialog(config: {
    title: string;
    message: string;
    confirmText: string;
    confirmColor?: 'primary' | 'warn' | 'accent';
    showCancel?: boolean;
    onConfirm?: () => void;
  }): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationConfig = {
        title: config.title,
        message: config.message,
        confirmText: config.confirmText,
        confirmColor: config.confirmColor || 'primary',
        showCancel: config.showCancel !== false,
        action: () => {
          // Call the onConfirm callback if provided
          if (config.onConfirm) {
            config.onConfirm();
          }
          resolve(true);
        }
      };
      this.showConfirmation = true;

      // Store the resolve function so cancelConfirmation can access it
      this.currentConfirmationResolve = resolve;
    });
  }


  executeConfirmation(): void {
    if (this.confirmationConfig.action) {
      this.confirmationConfig.action();
    }
    this.cancelConfirmation();
  }

  onSubmitClick(): void {
    if (this.isDocumentTotalMissing()) {
      this.showMissingDocumentTotalDialog();
      return;
    }

    const incompleteCheck = this.getIncompleteLineItemStats();
    const totalValidation = this.validateStatementAmountTotal();

    if (incompleteCheck.hasIncomplete || (!this.supplierSkipLineItems && !totalValidation.isValid)) {
      this.confirmSubmit();
    } else {
      this.submit();
    }
  }
  private getIncompleteLineItemStats(): { hasIncomplete: boolean; incompleteCount: number; totalCount: number } {
    const incompleteCount = this.lineItems.reduce((count, item) => {
      return this.isLineItemIncomplete(item) ? count + 1 : count;
    }, 0);

    return {
      hasIncomplete: incompleteCount > 0,
      incompleteCount,
      totalCount: this.lineItems.length
    };
  }

  isLineItemIncomplete(item: LineItem): boolean {
    if (!item) {
      return false;
    }

    const hasDocumentDate = Boolean(item.documentDate?.trim());
    const hasDocumentNumber = Boolean(item.documentNumber?.trim());
    const hasDocumentType = Boolean(item.documentType?.trim());
    const hasAmount = this.hasNumericValue(item.amount);

    return !(hasDocumentDate && hasDocumentType && hasDocumentNumber && hasAmount);
  }

  private hasNumericValue(value: number | null | undefined): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    return !isNaN(Number(value));
  }

  private isDocumentTotalMissing(): boolean {
    return this.documentTotal === null || this.documentTotal === undefined;
  }

  validateStatementAmountTotal(): { isValid: boolean; variance: number } {
    this.statementAmountTotal = this.lineItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    const documentTotal = Number(this.documentTotal) || 0;
    this.statementAmountVariance = Math.abs(documentTotal - this.statementAmountTotal);

    this.statementAmountTotalValid = this.statementAmountVariance < this.validationThreshold;

    return {
      isValid: this.statementAmountTotalValid,
      variance: this.statementAmountVariance
    };
  }

  confirmSubmit(): void {
    const itemCount = this.lineItems.length;
    const totalsAreValid = this.statementAmountTotalValid;
    const validationText = totalsAreValid ? 'All totals match.' : 'There are validation errors with the totals.';

    const incompleteCheck = this.getIncompleteLineItemStats();
    let incompleteWarning = '';

    if (incompleteCheck.hasIncomplete) {
      if (incompleteCheck.incompleteCount === incompleteCheck.totalCount) {
        incompleteWarning = ' All line items are incomplete.';
      } else {
        incompleteWarning = ` ${incompleteCheck.incompleteCount} of ${incompleteCheck.totalCount} line items are incomplete.`;
      }
    }

    this.confirmationConfig = {
      title: 'Submit Line Items',
      message: `Submit ${itemCount} line item${itemCount !== 1 ? 's' : ''}? ${validationText}${incompleteWarning}`,
      confirmText: 'Submit',
      confirmColor: totalsAreValid ? 'primary' : 'warn',
      showCancel: true,
      action: () => this.submit()
    };
    this.showConfirmation = true;
  }

  private showMissingDocumentTotalDialog(): void {
    this.confirmationConfig = {
      title: 'Submit Line Items',
      message: 'Document total is missing. Please review.',
      confirmText: 'OK',
      confirmColor: 'primary',
      showCancel: false,
      action: null
    };
    this.showConfirmation = true;
  }

  cancelConfirmation(): void {
    this.showConfirmation = false;
    this.confirmationConfig = {
      title: '',
      message: '',
      confirmText: '',
      confirmColor: 'primary',
      showCancel: true,
      action: null
    };

    // Handle Promise resolution for cancellation
    if (this.currentConfirmationResolve) {
      this.currentConfirmationResolve(false);
      this.currentConfirmationResolve = null;
    }
  }

  confirmStopJob(): void {
    this.showConfirmationDialog({
      title: 'Stop Job',
      message: 'Are you sure you want to stop the current job? Any unsaved changes will be lost.',
      confirmText: 'Stop Job',
      confirmColor: 'warn',
      onConfirm: () => this.releaseFile()
    });
  }

  // ============================== Column visibility
  onSelectedColumnsChange() {
    // Ensure selected columns are arranged based on their original order in allColumns
    this.displayedColumns.sort((a, b) => {
      const indexA = this.allColumns.findIndex(col => col.field === a.field);
      const indexB = this.allColumns.findIndex(col => col.field === b.field);
      return indexA - indexB;
    });
    this.updatePresetFromSelection();
    this.displayedColCount = this.displayedColumns.length + 1;
  }

  onPresetChange(event: { value: ColumnPreset }): void {
    const preset = event.value;
    if (preset && preset.columnFields) {
      this.displayedColumns = this.allColumns.filter(col =>
        preset.columnFields.includes(col.field)
      );
    }
    this.displayedColCount = this.displayedColumns.length + 1;
  }

  private updatePresetFromSelection(): void {
    const selectedFields = new Set(this.displayedColumns.map(c => c.field));

    const matchedPreset = this.columnPresets.find(preset => {
      if (preset.name === 'Custom') return false; // Skip custom preset for matching

      const presetFields = new Set(preset.columnFields);
      if (selectedFields.size !== presetFields.size) return false;

      for (const field of selectedFields) {
        if (!presetFields.has(field)) {
          return false;
        }
      }
      return true;
    });

    this.selectedPreset = matchedPreset || {};
  }

  // ============================== Keyboard shortcut handlers
  @HostListener('document:keydown.f9', ['$event'])
  onF9Key(event: KeyboardEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.togglePdfPanel();
  }

  @HostListener('document:keydown.f10', ['$event'])
  onF10Key(event: KeyboardEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggleTablePanel();
  }

  @HostListener('document:keydown.f11', ['$event'])
  onF11Key(event: KeyboardEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggleJobControls();
  }

  @HostListener('document:keydown.f1', ['$event'])
  onF1Key(event: KeyboardEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggleHelp();
  }

  @HostListener('window:beforeunload', ['$event'])
  OnClose($event: any): void {
    if (this.currentInputDocument !== undefined) $event.returnValue = 'Are you sure you want to leave?';
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    this.handleGlobalKeyboard(event);

    if (event.key === 'Insert') {
      if (event.ctrlKey && event.shiftKey && !event.altKey && !event.metaKey) {
        event.preventDefault();
        this.addLineItemAtTop();
        return;
      }

      if (event.ctrlKey && !event.shiftKey && !event.altKey && !event.metaKey) {
        event.preventDefault();
        this.addLineItemAtBottom();
        return;
      }

      if (!event.ctrlKey && !event.shiftKey && !event.altKey && !event.metaKey) {
        event.preventDefault();
        const currentIndex = document.activeElement?.closest('tr') ?
          Array.from(document.activeElement.closest('tbody').querySelectorAll('tr')).indexOf(document.activeElement.closest('tr')) :
          this.lineItems.length - 1;
        this.insertRowAfter(currentIndex);
        return;
      }
      return;
    }
  }

  private handleGlobalKeyboard(event: KeyboardEvent): void {
    if (!event.ctrlKey) return;
    const key = event.key.toLowerCase();

    switch (key) {
    case 'enter':
      event.preventDefault();
      this.onSubmitClick();
      break;

    case 'f':
      event.preventDefault();
      this.focusTableFilter();
      break;
    }
  }

  private focusTableFilter(): void {
    // Focus the table filter input based on your HTML structure
    const filterInput = document.querySelector('.table-search input[placeholder="Filter by keyword"]') as HTMLInputElement;
    if (filterInput) {
      filterInput.focus();
      filterInput.select();
    }
  }

  showKeyboardShortcutsPanel(): void {
    // Clear any existing help toast first
    if (this.helpToastRef) {
      this.toastr.clear(this.helpToastRef.toastId);
      this.helpToastRef = null;
    }

    const shortcutsHtml = `
  <div class="shortcuts-help">
    <div class="shortcuts-grid">
      <div class="shortcut-section">
        <h4>Navigation</h4>
        <div class="shortcut-item"><span>Move Up/Down</span><kbd>↑ ↓</kbd></div>
        <div class="shortcut-item"><span>Next Cell</span><kbd>Tab</kbd></div>
        <div class="shortcut-item"><span>Previous Cell</span><kbd>Shift+Tab</kbd></div>
        <div class="shortcut-item"><span>Next Page</span><kbd>PageDown</kbd></div>
        <div class="shortcut-item"><span>Previous Page</span><kbd>PageUp</kbd></div>
      </div>
      <div class="shortcut-section">
        <h4>Row Operations</h4>
        <div class="shortcut-item"><span>Insert Row Below</span><kbd>Insert</kbd></div>
        <div class="shortcut-item"><span>Insert Row at Top</span><kbd>Ctrl+Shift+Insert</kbd></div>
        <div class="shortcut-item"><span>Insert Row at Bottom</span><kbd>Ctrl+Insert</kbd></div>
      </div>
      <div class="shortcut-section">
        <h4>Panel & Global</h4>
        <div class="shortcut-item"><span>Toggle PDF Panel</span><kbd>F9</kbd></div>
        <div class="shortcut-item"><span>Toggle Table Panel</span><kbd>F10</kbd></div>
        <div class="shortcut-item"><span>Toggle Job Controls</span><kbd>F11</kbd></div>
        <div class="shortcut-item"><span>Submit</span><kbd>Ctrl+Enter</kbd></div>
        <div class="shortcut-item"><span>Focus Filter</span><kbd>Ctrl+F</kbd></div>
        <div class="shortcut-item"><span>Help</span><kbd>F1</kbd></div>
      </div>
    </div>
    <div class="shortcuts-grid">
      <div class="shortcut-section debug-section">
        <h4>Debug Information</h4>
        <div class="shortcut-item debug-item"><span>Document ID</span><kbd>${this.documentId || 'Not set'}</kbd></div>
        <div class="shortcut-item debug-item"><span>File UUID</span><kbd>${this.uuid || 'Not set'}</kbd></div>
        <div class="shortcut-item debug-item"><span>Line Items Count</span><kbd>${this.lineItems?.length || 0}</kbd></div>
        <div class="shortcut-item debug-item"><span>Supplier</span><kbd>${this.supplierName || 'Not set'}</kbd></div>
      </div>
    </div>
  </div>`;

    this.helpToastRef = this.toastr.info(shortcutsHtml, '', {
      timeOut: 0,
      extendedTimeOut: 0,
      closeButton: true,
      enableHtml: true,
      positionClass: 'toast-top-center',
      toastClass: 'ngx-toastr shortcuts-toast',
      tapToDismiss: false,
      disableTimeOut: true
    });

    this.isHelpVisible = true;

    if (this.helpToastRef && this.helpToastRef.onHidden) {
      this.helpToastRef.onHidden.subscribe(() => {
        this.helpToastRef = null;
        this.isHelpVisible = false;
      });
    }
  }

  // ============================== Panel sizing/visibility handlers
  togglePdfPanel(): void {
    this.currentPanelSizes[0] > 1 ? this.minimizePdfPanel() : this.expandPdfPanel();
  }
  toggleTablePanel(): void {
    this.currentPanelSizes[1] > 1 ? this.minimizeTablePanel() : this.expandTablePanel();
  }
  minimizePdfPanel(): void {
    this.applyPanelSizes(this.leftCollapsed);
  }
  expandPdfPanel(): void {
    this.applyPanelSizes(this.preferredPanelSizes);
    this.fitPdfToWidth();
  }
  minimizeTablePanel(): void {
    this.applyPanelSizes(this.rightCollapsed);
    this.fitPdfToWidth();
  }
  expandTablePanel(): void {
    this.applyPanelSizes(this.preferredPanelSizes);
    this.fitPdfToWidth();
  }
  onSplitterResizeEnd(event: SplitterResizeEndEvent): void {
    if (event?.sizes?.length === 2) {
      this.applyPanelSizes(event.sizes, true);
      this.fitPdfToWidth();
    }
  }
  private applyPanelSizes(sizes: number[], persistPreference = false): void {
    this.currentPanelSizes = [...sizes];
    if (persistPreference) {
      this.preferredPanelSizes = [...sizes];
      this.persistPreferredPanelSizes();
    }
  }
  private restorePanelSizesFromStorage(): void {
    const storedSizes = this.getStoredPanelSizes();
    if (storedSizes) {
      this.preferredPanelSizes = [...storedSizes];
      this.applyPanelSizes(storedSizes);
      return;
    }

    this.preferredPanelSizes = [...this.equalSized];
    this.applyPanelSizes(this.equalSized);
    this.persistPreferredPanelSizes();
  }
  private getStoredPanelSizes(): number[] | null {
    if (!this.canAccessLocalStorage()) {
      return null;
    }

    try {
      const storedValue = localStorage.getItem(this.panelSizeStorageKey);
      if (!storedValue) {
        return null;
      }

      const parsed = JSON.parse(storedValue);
      if (Array.isArray(parsed) && parsed.length === 2) {
        const normalized = parsed.map(Number);
        if (normalized.every(value => !isNaN(value))) {
          return normalized;
        }
      }
    } catch (error) {
      this.logger.warn(`Failed to read persisted panel sizes: ${error}`);
    }

    return null;
  }
  private persistPreferredPanelSizes(): void {
    if (!this.canAccessLocalStorage()) {
      return;
    }

    try {
      localStorage.setItem(this.panelSizeStorageKey, JSON.stringify(this.preferredPanelSizes));
    } catch (error) {
      this.logger.warn(`Failed to persist panel sizes: ${error}`);
    }
  }
  private canAccessLocalStorage(): boolean {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  }
  fitPdfToWidth() {
    this.schedulePdfZoomToFit(10);
  }

  private schedulePdfZoomToFit(delay = 0): void {
    setTimeout(() => {
      this.pdfViewer?.zoomToFit();
    }, delay);
  }

  toggleJobControls(): void {
    this.isJobControlsHidden = !this.isJobControlsHidden;
  }

  toggleHelp(): void {
    if (this.isHelpVisible && this.helpToastRef) {
      // Close existing toast
      this.toastr.clear(this.helpToastRef.toastId);
      this.helpToastRef = null;
      this.isHelpVisible = false;
    } else if (!this.isHelpVisible) {
      this.showKeyboardShortcutsPanel();
    }
  }

  // ============================== Data table action handlers
  onNumberInput(_event: any, _field: string): void {
  }
  onEditComplete(event: any) {
    const field = event.field;
    const id = event.data.id;
    const key = `${id}-${field}`;
    const originalValue = this.originalDataSnapshot.get(key);
    const currentValue = event.data[event.field];

    const originalStr = String(originalValue || '').trim();
    const currentStr = String(currentValue || '').trim();

    if (originalStr !== currentStr) {
      // Cell is different from original
      this.changedCells.add(key);
    } else {
      // Cell matches original - remove from changed set
      this.changedCells.delete(key);
    }

    // Efficient check: if any cells are changed, mark as unsaved
    this.hasUnsavedTableChanges = this.changedCells.size > 0;

    if (field === 'amount') {
      this.validateStatementAmountTotal();
    }
  }
  addLineItemAtTop(): void {
    const newItem = this.createEmptyLineItem();
    this.lineItems.unshift(newItem);
    this.hasUnsavedTableChanges = true;
    setTimeout(() => this.focusTableCell(0, 0), 50);
  }
  addLineItemAtBottom(): void {
    const newItem = this.createEmptyLineItem();
    this.lineItems.push(newItem);
    this.hasUnsavedTableChanges = true;
    setTimeout(() => this.focusTableCell(this.lineItems.length - 1, 0), 50);
  }
  insertRowAfter(index: number): void {
    const newItem = this.createEmptyLineItem();
    this.lineItems.splice(index + 1, 0, newItem);
    this.hasUnsavedTableChanges = true;
    setTimeout(() => this.focusTableCell(index + 1, 0), 50);
  }

  deleteRow(index: number): void {
    if (index < 0 || index >= this.lineItems.length) return;

    const deletedItem = this.lineItems[index];

    Object.keys(deletedItem).forEach(field => {
      if (field === 'gridCellLocations') {
        return;
      }
      const key = `${deletedItem.id}-${field}`;
      this.originalDataSnapshot.delete(key);
      this.changedCells.delete(key);
    });

    // Update hasUnsavedTableChanges flag
    this.hasUnsavedTableChanges = this.changedCells.size > 0;

    this.lineItems.splice(index, 1);
    this.validateStatementAmountTotal();
  }

  deleteAllRows(): void {
    if (this.lineItems.length === 0) {
      this.toastr.info('Table is already empty');
      return;
    }

    this.lineItems = [];
    this.selectedChannel = '';

    // Clear change tracking since all data is gone
    this.originalDataSnapshot.clear();
    this.changedCells.clear();
    this.hasUnsavedTableChanges = false;
    this.validateStatementAmountTotal();
  }

  private focusTableCell(rowIndex: number, colIndex: number): void {
    try {
      // Use ChangeDetectorRef to ensure DOM is updated
      this.cdr.detectChanges();

      // Find the table body
      const tableBody = document.querySelector('p-table tbody.p-datatable-tbody');
      if (!tableBody) return;

      // Find the specific row
      const rows = tableBody.querySelectorAll('tr');
      if (!rows[rowIndex]) return;

      // Find the specific editable cell (skip action column)
      const editableCells = rows[rowIndex].querySelectorAll('td.p-editable-column');
      if (!editableCells[colIndex]) return;

      const targetCell = editableCells[colIndex] as HTMLElement;

      // First focus the cell to activate it
      targetCell.focus();

      // Then click to enter edit mode
      targetCell.click();

      // Wait a bit for PrimeNG to create the input element, then focus it
      setTimeout(() => {
        // Look for input elements within the cell editor
        const input = targetCell.querySelector('input[pinputtext], input.p-inputtext, input.p-inputnumber-input') as HTMLInputElement;
        if (input) {
          input.focus();
          input.select(); // Select all text if any exists
        }
      }, 50);

    } catch (error) {
      console.warn('Could not focus table cell:', error);
    }
  }

  private createEmptyLineItem(): LineItem {
    return {
      id: Date.now() + Math.random(),
      statementLineItemId: undefined,
      documentDate: '',
      documentType: '',
      documentNumber: '',
      amount: null,
      gridCellLocations: {}
    };
  }

  // Note : This is to trigger edit mode automatically when user tabs into the table
  onCellFocus(rowIndex: number, colIndex: number, event: FocusEvent) {
    // Set current cell edit whenever a cell is focused
    const column = this.displayedColumns[colIndex];
    if (column && this.lineItems[rowIndex]) {
      this.currentCellEdit = {
        rowData: this.lineItems[rowIndex],
        field: column.field,
        index: rowIndex
      };

      if (this.pdfViewer?.isGridMode) {
        const location = this.lineItems[rowIndex].gridCellLocations?.[column.field];
        if (location) {
          void this.pdfViewer.highlightCellOnPdf(location);
        }
      }
    }

    if (rowIndex === 0 && colIndex === 0) {
      const target = event.target as HTMLElement;
      target.click();
    }
  }

  onCellKeyDown(event: KeyboardEvent) {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.stopPropagation();
    }
  }

  // ============================== OCR handlers
  onTextExtracted(extractedData: ExtractedText): void {
    const isInputFocused = document.activeElement instanceof HTMLInputElement;
    if (!isInputFocused) {
      return;
    }

    if (!extractedData.text) {
      return;
    }
    const extractedText = extractedData.text.trim();

    const activeElement = document.activeElement as HTMLInputElement;
    const tableCell = activeElement.closest('td.p-editable-column');

    if (tableCell) {
      const row = tableCell.closest('tr');
      const tbody = row.parentElement;
      const rowIndex = Array.from(tbody.children).indexOf(row);

      // Find which column this cell represents
      const cellIndex = Array.from(row.querySelectorAll('td.p-editable-column')).indexOf(tableCell);

      if (this.lineItems[rowIndex] && this.displayedColumns[cellIndex]) {
        // Update currentCellEdit to match actual focused cell
        this.currentCellEdit = {
          rowData: this.lineItems[rowIndex],
          field: this.displayedColumns[cellIndex].field,
          index: rowIndex
        };
        this.setTableCellValue(extractedText);
      }
    } else if (activeElement.id) {
      // We're in a header field
      this.currentCellEdit = null;
      this.setHeaderFieldValue(activeElement.id, extractedText);
    }
  }


  private setTableCellValue(value: string) {
    let cleanedValue: string | number = value;
    const column = this.allColumns.find(c => c.field === this.currentCellEdit.field);
    if (this.currentCellEdit.field === 'documentDate') {
      if (!this.formatStatementDocumentDate(value)) {
        this.toastr.warning('Document Date is not valid');
        return;
      }
    } else if(column.type === 'integer' || column.type === 'decimal') {
      cleanedValue = this.parseNumericValue(value);
    } else if (column.type === 'boolean') {
      return;
    }
    this.currentCellEdit.rowData[this.currentCellEdit.field] = cleanedValue;
    this.markLineItemFieldChanged(this.currentCellEdit.rowData, this.currentCellEdit.field);
  }

  private setHeaderFieldValue(fieldName: string, value: string): void {
    switch (fieldName) {
    case 'documentTotal':
      this.documentTotal = this.parseNumericValue(value);
      this.validateStatementAmountTotal();
      break;
    }
  }

  private parseNumericValue(value: string): number {
    const cleanValue = value.replace(/[^\d.-]/g, '');
    const numericValue = parseFloat(cleanValue);
    return isNaN(numericValue) ? 0 : numericValue;
  }

  private markLineItemFieldChanged(lineItem: LineItem, field: string): void {
    const key = `${lineItem.id}-${field}`;
    const originalValue = this.originalDataSnapshot.get(key);
    const currentValue = lineItem[field];

    const originalStr = String(originalValue || '').trim();
    const currentStr = String(currentValue || '').trim();

    if (originalStr !== currentStr) {
      this.changedCells.add(key);
    } else {
      this.changedCells.delete(key);
    }

    this.hasUnsavedTableChanges = this.changedCells.size > 0;
  }

  // ============================== PDF event handlers
  onPdfOpenNewWindow(): void {
    this.minimizePdfPanel();
    setTimeout(() => this.openPdfInPopoutWindow(), 0);
  }

  onPdfMinimized(): void {  // This is triggered from the PDF viewer's internal minimize button
    this.minimizePdfPanel();
  }

  private captureOriginalSnapshot(): void {
    this.originalDataSnapshot.clear();
    this.changedCells.clear(); // Reset changed cells when loading new data

    this.lineItems.forEach(item => {
      Object.keys(item).forEach(field => {
        if (field === 'gridCellLocations') {
          return;
        }
        const key = `${item.id}-${field}`;
        this.originalDataSnapshot.set(key, item[field]);
      });
    });
  }

  private async getPdfUrl(uuid: string): Promise<string> {
    try {
      const endpoint = `${uuid}?pdfOnly=true`;
      const fileRequest = this.apiHandler.getABCisRequest<any>(
        ABCisApi.Files,
        endpoint,
        {responseType: 'arraybuffer' as 'json'}
      );

      const response = await fileRequest;
      const data = response.body;

      if (!data) {
        throw new Error('No PDF data received from server');
      }

      const fileURL = URL.createObjectURL(new Blob([data], {type: 'application/pdf'}));
      return fileURL;
    } catch (error) {
      throw new Error(`Failed to load PDF for document ${uuid}: ${error.message || error}`);
    }
  }

  async releaseFile(): Promise<void> {
    this.isloading = true;

    if (this.helpToastRef) {
      this.toastr.clear(this.helpToastRef.toastId);
      this.helpToastRef = null;
      this.isHelpVisible = false;
    }

    try {
      await this.apiHandler.postABCisRequest(ABCisApi.Ocr, `document/${this.currentInputDocument.uuid}/release`, undefined);

      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl);
        this.pdfUrl = '';
      }

      this.cleanupSessionStorage();
      this.currentInputDocument = undefined;
      this.uuid = undefined;
      this.resetForm();
    } catch (error) {
      this.logger.error(`Failed to release file: ${error.message || error}`);
      const httpError = error as HttpErrorResponse;
      this.toastr.error(httpError.error?.message || 'Failed to release file');
    } finally {
      this.isloading = false;
    }

    this.toggleJobControls();
  }

  async submit(): Promise<void> {
    if (!this.currentInputDocument) {
      this.toastr.warning('No document loaded');
      return;
    }

    if (this.isDocumentTotalMissing()) {
      this.showMissingDocumentTotalDialog();
      return;
    }

    if (!this.supplierSkipLineItems && this.lineItems.length === 0) {
      this.toastr.warning('No line items to submit');
      return;
    }

    const normalizedDocumentDates = this.lineItems.map(item =>
      item.documentDate?.trim() ? this.formatStatementDocumentDate(item.documentDate) : null
    );
    const invalidDocumentDateIndex = this.lineItems.findIndex((item, index) =>
      Boolean(item.documentDate?.trim()) && !normalizedDocumentDates[index]
    );
    if (invalidDocumentDateIndex !== -1) {
      this.toastr.warning(`Document Date is not valid on line ${invalidDocumentDateIndex + 1}`);
      return;
    }

    if (this.helpToastRef) {
      this.toastr.clear(this.helpToastRef.toastId);
      this.helpToastRef = null;
      this.isHelpVisible = false;
    }

    this.isloading = true;
    try {
      const payload = {
        uuid: this.currentInputDocument.uuid,
        assignToNextJob: true,
        channelId: this.webUiChannelId,
        data: {
          headerData: {
            documentId: this.documentId || null,
            documentTotal: this.documentTotal,
          },
          statementLineItemData: this.lineItems.map((item, index) => ({
            statementLineItemId: item.statementLineItemId || null,
            documentId: this.documentId || null,
            documentDate: normalizedDocumentDates[index],
            documentType: item.documentType || null,
            documentNumber: item.documentNumber || null,
            amount: item.amount ?? null
          }))
        }
      };

      await this.apiHandler.postExternalRequest(
        environment.cloudFunctionUrl,
        payload
      );
      this.toastr.success('Document submitted to OCR task queue');
      this.cleanupSessionStorage();
      await this.getNextFile();

    } catch(error) {
      this.logger.error(`Failed to enqueue OCR task: ${error.message || error}`);
      const httpError = error as HttpErrorResponse;
      this.toastr.error(httpError.error?.message || 'Failed to submit OCR task');
    } finally {
      this.isloading = false;
    }
  }

  // ============================== channel selection and data swapping hanlders
  async onChannelSelect(channelId: string): Promise<void> {
    if (!channelId || this.isChannelLoading) {
      return;
    }

    if (this.hasUnsavedTableChanges) {
      const confirmed = await this.showConfirmationDialog({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Switching channels will lose these changes. Continue?',
        confirmText: 'Switch Channel',
        confirmColor: 'warn'
      });

      if (!confirmed) {
        return;
      }
    }

    this.isChannelLoading = true;
    this.selectedChannel = channelId;

    try {
      this.resetFormDataForChannelSwitch();

      // Get data from cache instead of API call
      const channelData = this.allChannelData.find(
        channel => channel.channelId.toString() === channelId
      );

      if (channelData) {
        // Update header data from selected channel
        if (channelData.headerData) {
          this.populateHeaderData(channelData.headerData);
        }

        // Update line items
        if (channelData.lineItemData && channelData.lineItemData.length > 0) {
          this.populateLineItems(channelData.lineItemData);
          this.toastr.success(`Loaded ${channelData.lineItemData.length} line items from ${channelData.channelName}`);
        } else {
          this.lineItems = [];
        }
      }
    } catch (error) {
      this.logger.error(`Failed to switch channel: ${error.message || error}`);
    } finally {
      this.isChannelLoading = false;
    }
  }

  // ============================== Utilitiy methods
  private resetForm(): void {
    if (this.helpToastRef) {
      this.toastr.clear(this.helpToastRef.toastId);
      this.helpToastRef = null;
      this.isHelpVisible = false;
    }

    this.resetFormData();
    this.pdfUrl = '';
  }
  private resetFormData(): void {
    // Clear header fields
    this.documentTotal = null;
    this.documentId = null;
    this.supplierSkipLineItems = false;
    this.updateGridModeAvailability();
    this.statementAmountTotal = 0;
    this.statementAmountTotalValid = false;
    this.statementAmountVariance = 0;

    // Clear line items
    this.lineItems = [];

    // Clear change tracking
    this.originalDataSnapshot.clear();
    this.changedCells.clear();
    this.hasUnsavedTableChanges = false;

    // Clear channel data - these should be reset when loading new document or releasing file
    this.allChannelData = [];
    this.availableChannels = [];
    this.selectedChannel = '';

    // Clear table filter
    setTimeout(() => {
      const filterInput = document.querySelector('.table-search input') as HTMLInputElement;
      if (filterInput) {
        filterInput.value = '';
        // Trigger the filter reset on the table
        filterInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }, 100);
  }

  private resetFormDataForChannelSwitch(): void {
    this.documentTotal = null;
    this.documentId = null;
    this.statementAmountTotal = 0;
    this.statementAmountTotalValid = false;
    this.statementAmountVariance = 0;

    this.lineItems = [];

    this.originalDataSnapshot.clear();
    this.changedCells.clear();
    this.hasUnsavedTableChanges = false;
  }

  // ============================== Action Buttons
  onDiscardClick(): void {
    this.confirmationConfig = {
      title: 'Discard Document',
      message: 'Are you sure you want to discard this document? This action cannot be undone.',
      confirmText: 'Discard',
      confirmColor: 'warn',
      showCancel: true,
      action: () => this.performDiscard()
    };
    this.showConfirmation = true;
  }
  private async performDiscard(): Promise<void> {
    this.isloading = true;
    try {
      await this.apiHandler.postABCisRequest(
        ABCisApi.Ocr,
        `document/${this.currentInputDocument.uuid}/user-discard`
      );
      this.toastr.success('Document discarded successfully');
      this.cleanupSessionStorage();
      await this.getNextFile();
    } catch (error) {
      this.logger.error(`Failed to discard document: ${error.message || error}`);
      const httpError = error as HttpErrorResponse;
      this.toastr.error(httpError.error?.message || 'Failed to discard document');
      this.isloading = false;
    }
  }

  onRescanClick(): void {
    this.confirmationConfig = {
      title: 'Request Rescan',
      message: 'Are you sure you want to request a rescan of this document?',
      confirmText: 'Rescan',
      confirmColor: 'accent',
      showCancel: true,
      action: () => this.performRescan()
    };
    this.showConfirmation = true;
  }

  private async performRescan(): Promise<void> {
    this.isloading = true;
    try {
      await this.apiHandler.postABCisRequest(
        ABCisApi.Ocr,
        `document/${this.currentInputDocument.uuid}/request-rescan`
      );
      this.toastr.success('Rescan requested successfully');
      this.cleanupSessionStorage();
      await this.getNextFile();
    } catch (error) {
      this.logger.error(`Failed to request rescan: ${error.message || error}`);
      const httpError = error as HttpErrorResponse;
      this.toastr.error(httpError.error?.message || 'Failed to request rescan');
      this.isloading = false;
    }
  }

  onCategoryClick(): void {
    this.confirmationConfig = {
      title: 'Restore Document',
      message: 'Are you sure you want to restore this document for re-categorization?',
      confirmText: 'Restore',
      confirmColor: 'accent',
      showCancel: true,
      action: () => this.performCategory()
    };
    this.showConfirmation = true;
  }

  private async performCategory(): Promise<void> {
    this.isloading = true;
    try {
      await this.apiHandler.postABCisRequest(
        ABCisApi.Ocr,
        `document/${this.currentInputDocument.uuid}/redo-category-in-web`
      );
      this.toastr.success('Document restored successfully');
      this.cleanupSessionStorage();
      await this.getNextFile();
    } catch (error) {
      this.logger.error(`Failed to restore document: ${error.message || error}`);
      const httpError = error as HttpErrorResponse;
      this.toastr.error(httpError.error?.message || 'Failed to restore document');
      this.isloading = false;
    }
  }

  onEzeManualClick(): void {
    this.confirmationConfig = {
      title: 'Assign to Ezescan Manual Mode',
      message: 'Are you sure you want to assign this document to Ezescan manual mode for line item processing?',
      confirmText: 'Assign',
      confirmColor: 'accent',
      showCancel: true,
      action: () => this.performEzescanManual()
    };
    this.showConfirmation = true;
  }

  private async performEzescanManual(): Promise<void> {
    this.isloading = true;
    try {
      await this.apiHandler.postABCisRequest(
        ABCisApi.Ocr,
        `document/${this.currentInputDocument.uuid}/redo-lineitems-in-manual`
      );
      this.toastr.success('Document assigned to Ezescan manual mode successfully');
      this.cleanupSessionStorage();
      await this.getNextFile();
    } catch (error) {
      this.logger.error(`Failed to assign document to Ezescan manual mode: ${error.message || error}`);
      const httpError = error as HttpErrorResponse;
      this.toastr.error(httpError.error?.message || 'Failed to assign document to Ezescan manual mode');
      this.isloading = false;
    }
  }

  openClearColumnDialog(): void {
    this.clearColumnOptions = this.displayedColumns.map(col => ({
      label: col.header,
      value: col.field
    }));
    this.selectedClearColumn = '';
    this.showClearColumnDialog = true;
  }

  cancelClearColumn(): void {
    this.showClearColumnDialog = false;
    this.selectedClearColumn = '';
  }

  executeClearColumn(): void {
    if (!this.selectedClearColumn) {
      this.toastr.warning('Please select a column to clear');
      return;
    }

    this.lineItems.forEach(item => {
      if (Object.prototype.hasOwnProperty.call(item, this.selectedClearColumn)) {
        (item as any)[this.selectedClearColumn] = null;
      }
    });

    this.hasUnsavedTableChanges = true;

    this.toastr.success(`Cleared ${this.getColumnHeader(this.selectedClearColumn)} column`);
    this.cancelClearColumn();
  }

  openColumnSwapDialog(): void {
    // Populate swap options with currently displayed columns that can be swapped
    this.swapColumnOptions = this.displayedColumns
      .filter(col => col.type === 'string' || col.type === 'integer' || col.type === 'decimal')
      .map(col => ({
        label: col.header,
        value: col.field
      }));

    this.swapColumn1 = '';
    this.swapColumn2 = '';
    this.showColumnSwapDialog = true;
  }

  cancelColumnSwap(): void {
    this.showColumnSwapDialog = false;
    this.swapColumn1 = '';
    this.swapColumn2 = '';
  }

  executeColumnSwap(): void {
    if (!this.swapColumn1 || !this.swapColumn2 || this.swapColumn1 === this.swapColumn2) {
      this.toastr.warning('Please select two different columns to swap');
      return;
    }

    // Swap values across all rows
    this.lineItems.forEach(item => {
      const temp = item[this.swapColumn1];
      item[this.swapColumn1] = item[this.swapColumn2];
      item[this.swapColumn2] = temp;
    });

    this.hasUnsavedTableChanges = true;

    this.toastr.success(`Swapped ${this.getColumnHeader(this.swapColumn1)} with ${this.getColumnHeader(this.swapColumn2)}`);
    this.cancelColumnSwap();
  }

  private getColumnHeader(fieldName: string): string {
    const column = this.allColumns.find(col => col.field === fieldName);
    return column ? column.header : fieldName;
  }

  areColumnsCompatible(): boolean {
    if (!this.swapColumn1 || !this.swapColumn2) return false;

    const column1 = this.allColumns.find(col => col.field === this.swapColumn1);
    const column2 = this.allColumns.find(col => col.field === this.swapColumn2);

    return column1?.type === column2?.type;
  }

  private cleanupSessionStorage(): void {
    if (this.uuid) {
      // Clean up any existing session storage for this document
      sessionStorage.removeItem(this.uuid);
    }
  }
  onInputFocus(event: any): void {
    event.target.select();
  }

  onLineItemInputFocus(rowIndex: number, field: string, event: any): void {
    this.onInputFocus(event);
    const lineItem = this.lineItems[rowIndex];
    if (!lineItem || !this.pdfViewer?.isGridMode) {
      return;
    }

    const location = lineItem.gridCellLocations?.[field];
    if (location) {
      void this.pdfViewer.highlightCellOnPdf(location);
    }
  }

  // ============================== Grid Extraction handlers
  onGridExtracted(gridData: GridData): void {
    this.gridImportValidationMessage = '';
    this.gridExtractionPanelData = gridData;
    this.gridExtractionPanelPageKey = this.pdfViewer ? this.pdfViewer.getCurrentGridPageKey() : null;
    this.showGridExtractionPanel = true;
  }

  onGridRowIgnoreChange(event: any): void {
    this.logger.debug(`Row ignore change: ${event}`);
  }

  onGridImportData(result: any): void {
    this.logger.debug(`Grid data import with mappings: ${result}`);
    this.gridImportValidationMessage = '';

    // Handle close action
    if (result.action === 'close') {
      this.showGridExtractionPanel = false;
      return;
    }

    const { cells, columnMappings, mode } = result;
    const ignoredRows = new Set<number>(result.ignoredRows || []);

    // Group cells by row
    const rowMap = new Map<number, any[]>();
    cells.forEach((cell: any) => {
      if (!cell.ignored && !ignoredRows.has(cell.row)) {
        if (!rowMap.has(cell.row)) {
          rowMap.set(cell.row, []);
        }
        rowMap.get(cell.row)!.push(cell);
      }
    });

    // Convert to line items
    const newLineItems: any[] = [];

    rowMap.forEach((rowCells, rowIndex) => {
      const lineItem = this.createEmptyLineItem();

      // Map each cell to the appropriate field based on column mappings
      rowCells.forEach((cell: any) => {
        const mappedField = columnMappings[cell.col];
        if (mappedField) {
          const column = this.allColumns.find(c => c.field === mappedField);

          if (column) {
            let value: any = cell.text;

            // Convert value based on column type
            if (mappedField === 'documentDate') {
              if (!this.formatStatementDocumentDate(cell.text)) {
                this.gridImportValidationMessage = 'Document Date is not valid';
              }
            } else if (column.type === 'integer' || column.type === 'decimal') {
              value = this.parseNumericValue(cell.text);
            }

            lineItem[mappedField] = value;
            if (cell.location) {
              lineItem.gridCellLocations = lineItem.gridCellLocations || {};
              lineItem.gridCellLocations[mappedField] = cell.location;
            }
          }
        }
      });

      newLineItems.push(lineItem);
    });

    if (this.gridImportValidationMessage) {
      this.toastr.warning(this.gridImportValidationMessage);
      return;
    }

    // Add to existing line items
    if (newLineItems.length > 0) {
      let importMessage: string;
      if (mode === 'add') {
        // Add to existing line items (append to the end)
        this.lineItems = [...this.lineItems, ...newLineItems];
        importMessage = `Added ${newLineItems.length} new line items from grid`;
      } else {
        // Overwrite mode (default behavior)
        this.lineItems = newLineItems;
        importMessage = `Imported ${newLineItems.length} line items from grid (replaced existing)`;
      }
      this.hasUnsavedTableChanges = true;
      this.validateStatementAmountTotal();
      this.captureOriginalSnapshot();
      this.ensureMappedColumnsVisible(columnMappings);
      this.toastr.success(importMessage);
      this.logger.debug(importMessage);
    } else {
      this.toastr.warning('No valid line items to import');
    }

    this.showGridExtractionPanel = false;
  }

  // Accepts day-first numeric and English month-name dates; returns YYYY-MM-DD.
  private formatStatementDocumentDate(value: string): string | null {
    const rawValue = (value || '').trim();
    if (!rawValue) {
      return null;
    }

    const dayFirstMatch = rawValue.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/);
    if (dayFirstMatch) {
      const year = this.normalizeYear(Number(dayFirstMatch[3]));
      return this.buildFormattedDate(year, Number(dayFirstMatch[2]), Number(dayFirstMatch[1]));
    }

    // Also normalize dates previously stored in ISO format.
    const isoDateMatch = rawValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (isoDateMatch) {
      return this.buildFormattedDate(
        Number(isoDateMatch[1]),
        Number(isoDateMatch[2]),
        Number(isoDateMatch[3])
      );
    }

    const dayMonthNameMatch = rawValue.match(/^(\d{1,2})(?:\s+|-)([a-zA-Z]{3,})(?:\s+|-)(\d{4}|\d{2})$/);
    if (dayMonthNameMatch) {
      const month = this.getMonthNumber(dayMonthNameMatch[2]);
      if (!month) {
        return null;
      }

      const year = this.normalizeYear(Number(dayMonthNameMatch[3]));
      return this.buildFormattedDate(year, month, Number(dayMonthNameMatch[1]));
    }

    const monthDayNameMatch = rawValue.match(/^([a-zA-Z]{3,})(?:\s+|-)(\d{1,2}),?(?:\s+|-)(\d{4}|\d{2})$/);
    if (monthDayNameMatch) {
      const month = this.getMonthNumber(monthDayNameMatch[1]);
      if (!month) {
        return null;
      }

      const year = this.normalizeYear(Number(monthDayNameMatch[3]));
      return this.buildFormattedDate(year, month, Number(monthDayNameMatch[2]));
    }

    return null;
  }

  private getMonthNumber(monthName: string): number | null {
    const normalizedMonthName = monthName.toLowerCase();
    const monthMap: Record<string, number> = {
      jan: 1,
      january: 1,
      feb: 2,
      february: 2,
      mar: 3,
      march: 3,
      apr: 4,
      april: 4,
      may: 5,
      jun: 6,
      june: 6,
      jul: 7,
      july: 7,
      aug: 8,
      august: 8,
      sep: 9,
      sept: 9,
      september: 9,
      oct: 10,
      october: 10,
      nov: 11,
      november: 11,
      dec: 12,
      december: 12
    };

    return monthMap[normalizedMonthName] || null;
  }

  private normalizeYear(year: number): number {
    if (year >= 100) {
      return year;
    }

    return year >= 50 ? 1900 + year : 2000 + year;
  }

  private buildFormattedDate(year: number, month: number, day: number): string | null {
    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    const paddedMonth = `${month}`.padStart(2, '0');
    const paddedDay = `${day}`.padStart(2, '0');
    return `${year}-${paddedMonth}-${paddedDay}`;
  }

  async onGridCellClick(event: { cell: GridCell; location: GridHighlightLocation }): Promise<void> {
    if (!this.pdfViewer) {
      this.logger.warn('PDF viewer not available to highlight cell.');
      return;
    }

    try {
      await this.pdfViewer.highlightCellOnPdf(event.location);
    } catch (error) {
      this.logger.error('Failed to highlight cell on PDF viewer', error);
    }
  }

  private ensureMappedColumnsVisible(
    columnMappings: Record<number, string | null | undefined> | Array<string | null | undefined> | null | undefined
  ): void {
    if (!columnMappings) {
      return;
    }

    const mappingValues = Array.isArray(columnMappings)
      ? columnMappings
      : Object.values(columnMappings);

    const mappedFields = mappingValues.filter((field): field is string => !!field);
    if (!mappedFields.length) {
      return;
    }

    let columnsUpdated = false;
    mappedFields.forEach(field => {
      const alreadyVisible = this.displayedColumns.some(col => col.field === field);
      if (!alreadyVisible) {
        const columnDef = this.allColumns.find(col => col.field === field);
        if (columnDef) {
          this.displayedColumns.push(columnDef);
          columnsUpdated = true;
        }
      }
    });

    if (columnsUpdated) {
      this.onSelectedColumnsChange();
    }
  }
}
